import { useEffect, useRef, useState } from 'react';

// 72h Kp forecast strip, replaces LaunchTimeline + TimeScrubber + LayerMatrix
// in solar mode. Shows 24h history (cyan) + 72h forecast (amber) + CME arrival
// markers (violet). Click a CME marker to surface that event in the right
// panel via onSelectEvent.

export default function SolarForecastStrip({ weather, forecast, cmes = [], onSelectEvent }) {
  const wrap = useRef(null);
  const [size, setSize] = useState({ w: 1200, h: 200 });

  useEffect(() => {
    if (!wrap.current) return;
    const obs = new ResizeObserver(() => {
      if (!wrap.current) return;
      const r = wrap.current.getBoundingClientRect();
      setSize({ w: r.width, h: r.height });
    });
    obs.observe(wrap.current);
    return () => obs.disconnect();
  }, []);

  const history = weather?.kp?.history || [];
  const fpoints = forecast?.points || [];

  // Below ~480px the head row (title + legend) wraps to two lines, reserve
  // extra top padding so the chart's Kp 9 line doesn't sit under the legend.
  const PADL = 60, PADR = 36, PADT = size.w < 480 ? 52 : 30, PADB = 32;
  const innerW = Math.max(100, size.w - PADL - PADR);
  const innerH = Math.max(40, size.h - PADT - PADB);

  // x window: 24h back .. 72h forward
  const nowMs = Date.now();
  const t0    = nowMs - 24 * 3600 * 1000;
  const t1    = nowMs + 72 * 3600 * 1000;

  const xAt = (t) => PADL + ((t - t0) / (t1 - t0)) * innerW;
  const kpY = (v) => PADT + innerH - (Math.min(9, Math.max(0, v)) / 9) * innerH;

  const histPath = history
    .filter(p => new Date(p.t).getTime() >= t0)
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(new Date(p.t).getTime())} ${kpY(p.kp)}`)
    .join(' ');

  const fcPath = fpoints
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(new Date(p.t).getTime())} ${kpY(p.kp)}`)
    .join(' ');

  const fcFill = fpoints.length
    ? fcPath
      + ` L ${xAt(new Date(fpoints[fpoints.length - 1].t).getTime())} ${PADT + innerH}`
      + ` L ${xAt(new Date(fpoints[0].t).getTime())} ${PADT + innerH} Z`
    : '';

  const xticks = [
    { t: nowMs - 24 * 3600000, label: '−24h' },
    { t: nowMs - 12 * 3600000, label: '−12h' },
    { t: nowMs,                label: 'NOW',  emph: true },
    { t: nowMs + 12 * 3600000, label: '+12h' },
    { t: nowMs + 24 * 3600000, label: '+24h' },
    { t: nowMs + 48 * 3600000, label: '+48h' },
    { t: nowMs + 72 * 3600000, label: '+72h' },
  ];

  const yticks = [0, 3, 5, 7, 9];

  const inboundCmes = cmes.filter(c =>
    c.inbound && c.hours_to_arrival != null && c.hours_to_arrival > 0 && c.hours_to_arrival < 72
  );

  return (
    <div className="hg-strip" ref={wrap}>

      <div className="hg-strip-head">
        <div className="hg-fc-title">
          <span className="num">→</span>72-HOUR OUTLOOK
        </div>
        <div className="hg-fc-leg">
          <span><span className="swatch" style={{ background: '#7faec8' }} />24h history</span>
          <span><span className="swatch" style={{ background: '#e9b35d' }} />Kp forecast (NOAA)</span>
          <span><span className="swatch" style={{ background: '#a698d8' }} />CME arrival</span>
        </div>
      </div>

      <svg width="100%" height="100%" viewBox={`0 0 ${size.w} ${size.h}`}
           preserveAspectRatio="none" style={{ position: 'absolute', inset: 0 }}>
        <defs>
          <linearGradient id="stripKpFill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%"   stopColor="rgba(233,179,93,0.18)" />
            <stop offset="100%" stopColor="rgba(233,179,93,0)" />
          </linearGradient>
        </defs>

        {/* y gridlines */}
        {yticks.map(v => (
          <g key={v}>
            <line x1={PADL} y1={kpY(v)} x2={PADL + innerW} y2={kpY(v)}
                  stroke={`rgba(232,227,216,${v === 5 ? 0.10 : 0.04})`}
                  strokeWidth="0.5" strokeDasharray={v === 5 ? '2 3' : ''} />
            <text x={PADL - 8} y={kpY(v) + 3} textAnchor="end"
                  style={{ fontFamily: 'IBM Plex Mono', fontSize: 9.5, fill: '#c8c2b3', letterSpacing: 0.4 }}>
              Kp {v}
            </text>
          </g>
        ))}

        {/* x ticks */}
        {xticks.map((tk, i) => (
          <g key={i}>
            <line x1={xAt(tk.t)} y1={PADT} x2={xAt(tk.t)} y2={PADT + innerH}
                  stroke={`rgba(232,227,216,${tk.emph ? 0.22 : 0.06})`}
                  strokeWidth="0.5" strokeDasharray={tk.emph ? '' : '2 3'} />
            <text x={xAt(tk.t)} y={PADT + innerH + 14} textAnchor="middle"
                  style={{
                    fontFamily: 'IBM Plex Mono', fontSize: 9.5, letterSpacing: 0.6,
                    fill: tk.emph ? '#fbf8f0' : '#c8c2b3',
                  }}>
              {tk.label}
            </text>
          </g>
        ))}

        {/* CME arrival vertical markers */}
        {inboundCmes.map((c, i) => {
          const x = xAt(new Date(c.earth_arrival_time).getTime());
          return (
            <g key={i} style={{ cursor: 'pointer' }}
               onClick={() => onSelectEvent?.({
                 id:    `cme:${c.activity_id}`,
                 kind:  'cme',
                 tone:  c.earth_is_glancing_blow ? '' : 'rose',
                 hours: c.hours_to_arrival,
                 title: 'CME shock front arrives',
                 meta:  c.earth_is_glancing_blow ? 'glancing blow' : 'direct impact',
                 payload: c,
               })}>
              <line x1={x} y1={PADT} x2={x} y2={PADT + innerH}
                    stroke="#a698d8" strokeWidth="0.6" strokeDasharray="2 3" opacity="0.75" />
              <circle cx={x} cy={PADT + 10} r="3" fill="#a698d8" />
              <text x={x + 7} y={PADT + 13}
                    style={{ fontFamily: 'IBM Plex Mono', fontSize: 9, fill: '#a698d8', letterSpacing: 0.5 }}>
                CME T−{c.hours_to_arrival.toFixed(0)}h
              </text>
            </g>
          );
        })}

        {/* fills + curves */}
        {fcFill   && <path d={fcFill}   fill="url(#stripKpFill)" />}
        {histPath && <path d={histPath} fill="none" stroke="#7faec8" strokeWidth="1.1" opacity="0.65" />}
        {fcPath   && <path d={fcPath}   fill="none" stroke="#e9b35d" strokeWidth="1.6" />}

        {/* now marker */}
        <circle cx={xAt(nowMs)}
                cy={kpY(history.length ? history[history.length - 1].kp : 0)}
                r="3.5" fill="#e9b35d" />
      </svg>
    </div>
  );
}
